/**
 * Coach plan engine — shared between web and mobile.
 *
 * Builds a prescribed daily plan by running a PlanTemplate's stop builders in
 * order. Pure functions — no I/O, no React, no platform deps.
 */

import {
  MVP_NODES,
  type Item,
  type CurriculumNode,
} from "./item-library.js";
import {
  unlockedNodes,
  type CardStore,
  type MasteryThresholds,
} from "./scheduler.js";
import { nodeExerciseRoute, type ReviewSummary } from "./skill-state.js";
import {
  type PlanTemplate,
  type PlanBuildData,
  type SessionMix,
  ITEMS_PER_MINUTE,
} from "./plan-template.js";
import { resolveTemplate } from "./performance-analyzer.js";

export type {
  SessionMix,
  PlanStop,
  PlanStopNodeAcquisition,
  CoachPlan,
  PlanConfig,
  PlanBuildData,
  StopBuilder,
  PlanTemplate,
} from "./plan-template.js";

// ─── WeakSpot (standalone — not template-driven) ──────────────────────────────

export interface WeakSpot {
  node: CurriculumNode;
  lapseRate: number;
  totalReps: number;
  drillRoute: string;
}

// ─── buildCoachPlan ───────────────────────────────────────────────────────────

/**
 * Build a Coach's Plan by running the template's stop builders in order.
 *
 * @param template             - Selects node set, stop composition, and tuning config.
 * @param input.items          - All curriculum items (or filtered subset).
 * @param input.cards          - User's FSRS card store.
 * @param input.thresholds     - Mastery tier thresholds (from user pref, not template).
 * @param input.reviews        - Last 7+ days of reviews for warm-up lapse detection.
 * @param input.sessionTargetMinutes - User's session length pref.
 * @param input.sessionMix     - "review-only" / "balanced" / "new-focus".
 * @param input.newSkillNodeId - Pinned new-skill node (or null for auto).
 * @param input.nodeExposures  - Total Listen + Drill play count per node ID.
 * @param input.now            - Injectable current time for testing.
 */
/**
 * @internal — called by FsrsPlanGenerator. Public callers use buildCoachPlan.
 */
export function buildCoachPlanInternal(
  template: PlanTemplate,
  input: {
    items: Item[];
    cards: CardStore;
    thresholds: MasteryThresholds;
    reviews: readonly ReviewSummary[];
    sessionTargetMinutes: number;
    sessionMix: SessionMix;
    newSkillNodeId?: string | null;
    nodeExposures?: ReadonlyMap<string, number>;
    forceAcquired?: boolean;
    now?: Date;
  },
) {
  const now = input.now ?? new Date();
  const newSkillNodeId = input.newSkillNodeId ?? null;
  const nodeExposures = input.nodeExposures ?? new Map<string, number>();
  const forceAcquired = input.forceAcquired ?? false;

  // Filter A: restrict to nodes whose prerequisites are mastered/practicing.
  const unlockedNodeSet: ReadonlySet<string> = template.config.bypassFilterA
    ? new Set(template.nodes.map((n) => n.id))
    : new Set(
        unlockedNodes(template.nodes, input.items, input.cards, {
          thresholds: input.thresholds,
        }).map((n) => n.id),
      );

  // Filter B: require ≥ minNodeEngagement non-new cards per node.
  const engagedNodeSet: ReadonlySet<string> = template.config.bypassFilterB
    ? new Set(template.nodes.map((n) => n.id))
    : new Set(
        template.nodes
          .filter(
            (n) =>
              input.items.filter(
                (i) =>
                  i.nodeId === n.id &&
                  input.cards[i.id] !== undefined &&
                  input.cards[i.id]!.state !== "new",
              ).length >= template.config.minNodeEngagement,
          )
          .map((n) => n.id),
      );

  // Filter C: require ≥ minPracticeReviewsPerNode practice-sourced reviews.
  const practiceReviewedNodeSet: ReadonlySet<string> = template.config.bypassFilterC
    ? new Set(template.nodes.map((n) => n.id))
    : new Set(
        template.nodes
          .filter((n) => {
            const nodeItemIds = new Set(
              input.items.filter((i) => i.nodeId === n.id).map((i) => i.id),
            );
            let count = 0;
            for (const r of input.reviews) {
              if (!nodeItemIds.has(r.itemId)) continue;
              if (r.source === "practice") count++;
              if (count >= template.config.minPracticeReviewsPerNode) break;
            }
            return count >= template.config.minPracticeReviewsPerNode;
          })
          .map((n) => n.id),
      );

  const data: PlanBuildData = {
    items: input.items,
    cards: input.cards,
    thresholds: input.thresholds,
    reviews: input.reviews,
    sessionTargetMinutes: input.sessionTargetMinutes,
    sessionMix: input.sessionMix,
    newSkillNodeId,
    nodeExposures,
    forceAcquired,
    now,
    nodes: template.nodes,
    unlockedNodeSet,
    engagedNodeSet,
    practiceReviewedNodeSet,
  };

  const { resolvedTemplate, mode, interventionNodeTitle } = resolveTemplate(template, data);

  const alreadyPlanned = new Set<string>();
  const stops = [];

  for (const builder of resolvedTemplate.stops) {
    const stop = builder(data, resolvedTemplate.config, alreadyPlanned);
    if (stop !== null) {
      stops.push(stop);
      for (const item of stop.items) alreadyPlanned.add(item.id);
    }
  }

  const totalItemCount = stops.reduce((s, st) => s + st.itemCount, 0);
  return {
    stops,
    totalItemCount,
    totalEstimatedMinutes: Math.max(1, Math.round(totalItemCount / ITEMS_PER_MINUTE)),
    mode,
    interventionNodeTitle,
  };
}

/** Public backward-compat wrapper — callers should prefer the PlanGenerator API for new code. */
export function buildCoachPlan(
  template: PlanTemplate,
  input: Parameters<typeof buildCoachPlanInternal>[1],
) {
  return buildCoachPlanInternal(template, input);
}

// ─── top3WeakSpots (lifetime) ─────────────────────────────────────────────────

const WEAK_SPOT_MIN_REPS = 10;

/**
 * Return the top-3 curriculum nodes by lifetime lapse rate, filtered to
 * nodes with at least WEAK_SPOT_MIN_REPS total reviews.
 * Used by the Progress page for historical analysis.
 */
export function top3WeakSpots(
  items: Item[],
  cards: CardStore,
  nodes: CurriculumNode[] = MVP_NODES,
): WeakSpot[] {
  const stats = nodes
    .map((node) => {
      const nodeItems = items.filter((i) => i.nodeId === node.id);
      const totalReps = nodeItems.reduce((s, i) => s + (cards[i.id]?.reps ?? 0), 0);
      const totalLapses = nodeItems.reduce((s, i) => s + (cards[i.id]?.lapses ?? 0), 0);
      return {
        node,
        lapseRate: totalReps > 0 ? totalLapses / totalReps : null,
        totalReps,
      };
    })
    .filter(
      (s): s is { node: CurriculumNode; lapseRate: number; totalReps: number } =>
        s.lapseRate !== null && s.totalReps >= WEAK_SPOT_MIN_REPS,
    );

  return stats
    .sort((a, b) => b.lapseRate - a.lapseRate)
    .slice(0, 3)
    .map((s) => ({
      ...s,
      drillRoute: nodeToDrillRoute(s.node.id),
    }));
}

// ─── top3RecentWeakSpots (7-day) ──────────────────────────────────────────────

const RECENT_WEAK_SPOT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const RECENT_WEAK_SPOT_MIN_REPS = 5;

/**
 * Return the top-3 curriculum nodes by 7-day lapse rate, filtered to nodes
 * with at least RECENT_WEAK_SPOT_MIN_REPS reviews in the window.
 * Uses the same time window as the strengthen stop — so this widget and the
 * coach's plan always reflect identical data.
 * Used by the Practice Hub as the "needs work / drill now" shortcut.
 */
export function top3RecentWeakSpots(
  items: Item[],
  reviews: readonly ReviewSummary[],
  now: Date = new Date(),
  nodes: CurriculumNode[] = MVP_NODES,
): WeakSpot[] {
  const cutoff = new Date(now.getTime() - RECENT_WEAK_SPOT_WINDOW_MS);
  const recentReviews = reviews.filter((r) => new Date(r.timestamp) >= cutoff);

  const stats = nodes
    .map((node) => {
      const nodeItemIds = new Set(items.filter((i) => i.nodeId === node.id).map((i) => i.id));
      const nodeReviews = recentReviews.filter((r) => nodeItemIds.has(r.itemId));
      if (nodeReviews.length < RECENT_WEAK_SPOT_MIN_REPS) return null;
      const lapses = nodeReviews.filter((r) => !r.correct).length;
      if (lapses === 0) return null;
      return { node, lapseRate: lapses / nodeReviews.length, totalReps: nodeReviews.length };
    })
    .filter(
      (s): s is { node: CurriculumNode; lapseRate: number; totalReps: number } => s !== null,
    );

  return stats
    .sort((a, b) => b.lapseRate - a.lapseRate)
    .slice(0, 3)
    .map((s) => ({ ...s, drillRoute: nodeToDrillRoute(s.node.id) }));
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function nodeToDrillRoute(nodeId: string): string {
  const route = nodeExerciseRoute(nodeId);
  if (!route) return "/exercise";
  return `${route.path}?mode=listen&nodeId=${nodeId}`;
}
